import { $q } from '@uirouter/core';
import * as angular from 'angular';
import * as moment from 'moment';
import { ngStorage } from 'ngstorage';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../../../app.common/modules/common/services/popup.service';
import { AccountService } from '../../../account/account.service';
import {
    IExcludedCategory,
    IVoucherFilter,
    IVoucherItem,
    IVouchersList,
    VoucherStatusEnum,
    VoucherTypesEnum
} from '../../models/models';
import { VouchersService } from '../../vouchers.service';

class VouchersListComponent {
  public static $inject = [
    '$translate',
    'notificationService',
    'vouchersService',
    '$localStorage',
    'popupService',
    'accountService'
  ];

  public waiting: boolean = false;
  public canLoadMore: boolean = true;
  public total: number = 0;
  public vouchersList: IVoucherItem[] = [];
  public itemsLimit: number = 20;
  public voucherTypesEnum = VoucherTypesEnum;
  public voucherStatusEnum = VoucherStatusEnum;
  public countryID: number;
  public dateFormat = 'd MMMM yyyy';
  public isAdmin = false;
  private categories: IExcludedCategory[] = [];

  constructor(
    private $translate: angular.translate.ITranslateService,
    private notificationService: NotificationService,
    private vouchersService: VouchersService,
    private $localStorage: ngStorage.StorageService,
    private popupService: PopupService,
    private accountService: AccountService
  ) {}

  public async $onInit() {
    const response = await $q.all({
      isAdmin: this.accountService.isLoggedInAsAdmin(),
      allCategories: this.vouchersService.getClassificationCategories()
    });
    this.isAdmin = response.isAdmin as boolean;
    this.categories = response.allCategories.sort((a, b) => a.Name > b.Name ? 1 : a.Name < b.Name ? -1 : 0);
    this.countryID = this.$localStorage.retailerAuthorizationData.countryID;
    await this.loadData();
  }

  public async loadData() {
    if (this.waiting || !this.canLoadMore) {
      return;
    }

    this.waiting = true;

    this.notificationService.showWaitingMessage();

    const filter: IVoucherFilter = {
      limit: this.itemsLimit,
      offset: this.vouchersList ? this.vouchersList.length : 0
    };

    try {
      const response: IVouchersList = await this.vouchersService.getVouchersList(filter);
      this.total = response.Total;
      this.vouchersList = this.getVoucherList(response.Items);
      this.canLoadMore = response.Items.length === this.itemsLimit;
      this.waiting = false;
      this.notificationService.clearAll();
    } catch (error) {
      this.notificationService.showErrorMessage(error);
      this.waiting = false;
    }
  }

  public async publishVoucherConfirm(voucher: IVoucherItem) {
    try {
      const result = await this.popupService.confirm({
        title: this.$translate.instant('publish_voucher_title'),
        content: `<p style="word-break: break-all">
            <span>${this.$translate.instant('publish_voucher_content', {
              voucher: voucher.VoucherCode
            })}</span> <br>
            <span>${this.$translate.instant('voucher_publish_alert')}
            </span>
          </p>`,
        cancelClass: 'btn btn-with-icon btn-in-popup-footer',
        okClass: 'btn btn-with-icon btn-success flexVerticalAlignParent',
        classPopup: 'popup--redesign modal360',
        cancelTxt: this.$translate.instant('cancel'),
        okTxt: this.$translate.instant('voucher_publish'),
        hasIcon: true,
        thinTitle: true
      });

      if (result) {
        this.publishVoucher(voucher.VoucherID);
      }
    } catch (e) {
      return;
    }
  }

  public async deleteVoucherConfirm(voucher: IVoucherItem) {
    try {
      const result = await this.popupService.confirm({
        title: this.$translate.instant('delete_voucher_title'),
        content: `<span style="word-break: break-all">${this.$translate.instant(
          'delete_voucher_content',
          { voucher: voucher.VoucherCode }
        )}</span>`,
        cancelClass: 'btn btn-with-icon btn-in-popup-footer',
        okClass:
          'btn btn-with-icon btn-with-icon--before-delete btn-danger flexVerticalAlignParent',
        classPopup: 'popup--redesign modal360',
        cancelTxt: this.$translate.instant('cancel'),
        okTxt: this.$translate.instant('delete'),
        hasIcon: true,
        thinTitle: true
      });

      if (result) {
        this.deleteVoucher(voucher.VoucherID);
      }
    } catch (e) {
      return;
    }
  }

  public getExcludedCategoriesNames(voucher: IVoucherItem): string {
      return this.categories
          .filter((category) => !!voucher.RestrictedCategoriesIDs.find((id) => id === category.ID))
          .map((category) => category.Name)
          .join(', ');
  }

  private resetVouchersList() {
    this.canLoadMore = true;
    this.total = 0;
    this.vouchersList = [];
  }

  private getVoucherList(vouchersResponse: IVoucherItem[]): IVoucherItem[] {
    const formedVouchersList = vouchersResponse.map((voucher) => {
      voucher.IsExpired = moment
        .utc(voucher.EndDate)
        .isBefore(moment.utc(new Date()), 'date');
      return voucher;
    });

    return [...this.vouchersList, ...formedVouchersList];
  }

  private async publishVoucher(voucherId: number) {
    this.waiting = true;
    this.notificationService.showWaitingMessage();
    try {
      await this.vouchersService.publishVoucher(voucherId);
      this.waiting = false;
      this.notificationService.showSuccessMessage();
      this.resetVouchersList();
      this.loadData();
    } catch (error) {
      this.notificationService.showErrorMessage(error);
      this.waiting = false;
    }
  }

  private async deleteVoucher(voucherId: number) {
    this.waiting = true;
    this.notificationService.showWaitingMessage();
    try {
      await this.vouchersService.deleteVoucher(voucherId);
      this.waiting = false;
      this.notificationService.showSuccessMessage();
      this.resetVouchersList();
      this.loadData();
    } catch (error) {
      this.notificationService.showErrorMessage(error);
      this.waiting = false;
    }
  }
}

angular.module('naut').component('vouchersListComponent', {
  template: require('./vouchers-list.component.html'),
  controller: VouchersListComponent,
  controllerAs: 'ctrl'
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/vouchers/components/vouchers-list/vouchers-list.component.ts